import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function PotentialMatches({ item, itemType, onMatch, hotelId }) {
    const { t } = useTranslation();
    const [potentialMatches, setPotentialMatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMatch, setSelectedMatch] = useState(null);
    const [isStaffOrAdmin, setIsStaffOrAdmin] = useState(false);
    const [sortField, setSortField] = useState('matchScore');
    const [sortDirection, setSortDirection] = useState('desc');
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        async function checkUserRole() {
            if (currentUser) {
                const idTokenResult = await currentUser.getIdTokenResult();
                const isAdmin = idTokenResult.claims.admin === true;
                const isStaff = idTokenResult.claims.staff === true;
                setIsStaffOrAdmin(isAdmin || isStaff);
            }
        }
        checkUserRole();
    }, [currentUser]);

    useEffect(() => {
        async function fetchPotentialMatches() {
            if (!isStaffOrAdmin || !hotelId || !item) return;
            
            setLoading(true);
            try {
                const targetCollectionName = itemType === 'found' ? "lost-items" : "found-items";
                
                const q = query(
                    collection(db, targetCollectionName),
                    where("hotelId", "==", hotelId),
                    where("matchId", "==", null),
                    where("status", "==", "pending")
                );
                
                const querySnapshot = await getDocs(q);
                const matches = [];
                querySnapshot.forEach((doc) => {
                    const potentialMatchData = doc.data();
                    matches.push({
                        id: doc.id,
                        ...potentialMatchData
                    });
                });
                setPotentialMatches(matches);
            } catch (err) {
                console.error("Error fetching potential matches:", err);
            } finally {
                setLoading(false);
            }
        }

        fetchPotentialMatches();
    }, [isStaffOrAdmin, item, itemType, hotelId]);

    const handleMatch = async () => {
        if (selectedMatch) {
            onMatch(selectedMatch);
        }
    };

    const viewMatchDetails = (matchId) => {
        const targetPath = itemType === 'found' ? `/lost-items/${matchId}` : `/found-items/${matchId}`;
        navigate(`/${hotelId}${targetPath}`);
    };

    const handleRowClick = (match) => {
        viewMatchDetails(match.id);
    };

    const handleSelectMatch = (e, match) => {
        e.stopPropagation();
        setSelectedMatch(selectedMatch?.id === match.id ? null : match);
    };

    const formatDate = (dateString) => {
        if (!dateString) return t('potentialMatches.table.noData');
        try {
            if (dateString.seconds) {
                return new Date(dateString.seconds * 1000).toLocaleDateString();
            }
            return new Date(dateString).toLocaleDateString();
        } catch (error) {
            console.error('Error formatting date:', error);
            return t('potentialMatches.table.invalidDate');
        }
    };

    const getDateValue = (dateValue) => {
        if (!dateValue) return 0;
        if (dateValue.seconds) return dateValue.seconds * 1000;
        const parsed = new Date(dateValue);
        return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
    };

    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const renderSortableHeader = (label, field) => (
        <th
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {label}
            {sortField === field && (
                <span style={{ marginLeft: '5px' }}>
                    {sortDirection === 'asc' ? '▲' : '▼'}
                </span>
            )}
        </th>
    );

    const getSortValue = (match, field) => {
        switch (field) {
            case 'matchScore':
                return match.matchData.score;
            case 'item':
                return (match.itemName || match.itemType || '').toLowerCase();
            case 'date': {
                const dateValue = itemType === 'found' ? match.dateLost : match.dateFound;
                return getDateValue(dateValue);
            }
            case 'location': {
                const locationValue = itemType === 'found' ? match.locationLost : match.locationFound;
                return (locationValue || '').toLowerCase();
            }
            case 'owner': {
                const ownerValue = `${match.firstName || ''} ${match.surname || ''}`.trim();
                return ownerValue.toLowerCase();
            }
            case 'contact': {
                const contactValue = `${match.phone || ''} ${match.email || ''}`.trim();
                return contactValue.toLowerCase();
            }
            default:
                return '';
        }
    };

    // Calculate match score for a potential match
    const calculateMatchScore = (match) => {
        let score = 0;
        const criteria = {
            dateMatch: false,
            categoryMatch: false
        };

        // 1. Check if found date falls within stay date range (extended by 5 days after checkout)
        if (itemType === 'found' && match.dateLost && item.dateFound) {
            // For found items, check if found date is within the stay period of the lost item reporter
            const foundDate = new Date(item.dateFound.seconds ? item.dateFound.seconds * 1000 : item.dateFound);
            const stayFrom = match.stayDateFrom ? new Date(match.stayDateFrom.seconds ? match.stayDateFrom.seconds * 1000 : match.stayDateFrom) : null;
            const stayUntil = match.stayDateUntil ? new Date(match.stayDateUntil.seconds ? match.stayDateUntil.seconds * 1000 : match.stayDateUntil) : null;
            
            if (stayFrom && stayUntil) {
                // Extend stay period by 5 days after checkout
                const extendedStayUntil = new Date(stayUntil);
                extendedStayUntil.setDate(extendedStayUntil.getDate() + 5);
                
                if (foundDate >= stayFrom && foundDate <= extendedStayUntil) {
                    criteria.dateMatch = true;
                    score += 2; // Higher weight for date match
                }
            }
        } else if (itemType === 'lost' && match.dateFound && item.dateLost) {
            // For lost items, check if found date is within the stay period of the current lost item
            const lostDate = new Date(item.dateLost.seconds ? item.dateLost.seconds * 1000 : item.dateLost);
            const foundDate = new Date(match.dateFound.seconds ? match.dateFound.seconds * 1000 : match.dateFound);
            const stayFrom = item.stayDateFrom ? new Date(item.stayDateFrom.seconds ? item.stayDateFrom.seconds * 1000 : item.stayDateFrom) : null;
            const stayUntil = item.stayDateUntil ? new Date(item.stayDateUntil.seconds ? item.stayDateUntil.seconds * 1000 : item.stayDateUntil) : null;
            
            if (stayFrom && stayUntil) {
                // Extend stay period by 5 days after checkout
                const extendedStayUntil = new Date(stayUntil);
                extendedStayUntil.setDate(extendedStayUntil.getDate() + 5);
                
                if (foundDate >= stayFrom && foundDate <= extendedStayUntil) {
                    criteria.dateMatch = true;
                    score += 2; // Higher weight for date match
                }
            }
        }

        // 2. Check if item categories match
        if (item.itemType && match.itemType) {
            if (item.itemType.toLowerCase() === match.itemType.toLowerCase()) {
                criteria.categoryMatch = true;
                score += 1; // Lower weight for category match
            }
        }

        return { score, criteria };
    };

    const sortedMatches = potentialMatches
        .map(match => ({
            ...match,
            matchData: calculateMatchScore(match)
        }))
        .sort((a, b) => {
            const aValue = getSortValue(a, sortField);
            const bValue = getSortValue(b, sortField);
            if (aValue === bValue) {
                return b.matchData.score - a.matchData.score;
            }
            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : 1;
            }
            return aValue > bValue ? -1 : 1;
        });

    // Get row class based on match score
    const getRowClass = (matchData) => {
        const baseClass = `item-row clickable-row ${selectedMatch?.id === matchData.id ? 'selected-match' : ''}`;
        if (matchData.matchData.score >= 3) {
            return `${baseClass} excellent-match`;
        } else if (matchData.matchData.score >= 2) {
            return `${baseClass} good-match`;
        } else if (matchData.matchData.score >= 1) {
            return `${baseClass} partial-match`;
        }
        return baseClass;
    };

    // Render match score indicators
    const renderMatchScore = (matchData) => {
        const { score, criteria } = matchData;
        return (
            <div className="match-score">
                <div className="score-indicators">
                    <div className={`score-badge ${criteria.dateMatch ? 'match' : 'no-match'}`} title={criteria.dateMatch ? t('potentialMatches.tooltips.dateMatch') : t('potentialMatches.tooltips.dateNoMatch')}>
                        📅 {criteria.dateMatch ? '✓' : '✗'}
                    </div>
                    <div className={`score-badge ${criteria.categoryMatch ? 'match' : 'no-match'}`} title={criteria.categoryMatch ? t('potentialMatches.tooltips.categoryMatch') : t('potentialMatches.tooltips.categoryNoMatch')}>
                        🏷️ {criteria.categoryMatch ? '✓' : '✗'}
                    </div>
                </div>
                <div className="total-score" title={t('potentialMatches.tooltips.totalScore', { score, total: 3 })}>
                    {score === 3 && <span className="score-excellent">⭐⭐⭐</span>}
                    {score === 2 && <span className="score-good">⭐⭐</span>}
                    {score === 1 && <span className="score-partial">⭐</span>}
                    {score === 0 && <span className="score-none">—</span>}
                </div>
            </div>
        );
    };

    if (!isStaffOrAdmin) {
        return null;
    }

    if (loading) {
        return <div className="loading-container">{t('potentialMatches.loading')}</div>;
    }

    return (
        <div className="potential-matches-container">
            <h3>{t('potentialMatches.title', { itemName: item?.itemName })}</h3>
            {potentialMatches.length === 0 ? (
                <p>{t('potentialMatches.noMatches')}</p>
            ) : (
                <>
                    <div className="items-table-container">
                        <table className="items-table">
                            <thead>
                                <tr>
                                    {renderSortableHeader(t('potentialMatches.table.headers.matchScore'), 'matchScore')}
                                    <th>{t('potentialMatches.table.headers.image')}</th>
                                    {renderSortableHeader(t('potentialMatches.table.headers.item'), 'item')}
                                    {renderSortableHeader(itemType === 'found' ? t('potentialMatches.table.headers.lostDate') : t('potentialMatches.table.headers.foundDate'), 'date')}
                                    {renderSortableHeader(t('potentialMatches.table.headers.location'), 'location')}
                                    <th>{itemType === 'found' ? t('potentialMatches.table.headers.description') : t('potentialMatches.table.headers.storage')}</th>
                                    {renderSortableHeader(t('potentialMatches.table.headers.owner'), 'owner')}
                                    {renderSortableHeader(t('potentialMatches.table.headers.contact'), 'contact')}
                                    <th>{t('potentialMatches.table.headers.select')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedMatches.map((match) => (
                                    <tr
                                        key={match.id}
                                        className={getRowClass(match)}
                                        onClick={() => handleRowClick(match)}
                                    >
                                        <td className="match-score-cell">
                                            {renderMatchScore(match.matchData)}
                                        </td>
                                        <td className="item-thumbnail">
                                            {match.imageUrls && match.imageUrls.length > 0 ? (
                                                <img
                                                    src={match.imageUrls[0]}
                                                    alt={t('potentialMatches.table.thumbnailAlt', { itemName: match.itemName || t('potentialMatches.table.item') })}
                                                    className="thumbnail-image"
                                                />
                                            ) : (
                                                <div className="no-image">{t('potentialMatches.table.noImage')}</div>
                                            )}
                                        </td>
                                        <td className="item-cell">
                                            <div className="item-type">{match.itemType || t('potentialMatches.table.unknown')}</div>
                                            <div className="item-name">{match.itemName || t('potentialMatches.table.unnamedItem')}</div>
                                        </td>
                                        <td>
                                            {itemType === 'found' ? 
                                                formatDate(match.dateLost) : 
                                                formatDate(match.dateFound)
                                            }
                                        </td>
                                        <td>
                                            {itemType === 'found' ? 
                                                (match.locationLost || t('potentialMatches.table.unknown')) : 
                                                (match.locationFound || t('potentialMatches.table.unknown'))
                                            }
                                            {itemType !== 'found' && match.storageLocation && (
                                                <div className="storage-location">
                                                    <small>{t('potentialMatches.table.stored')}: {match.storageLocation}</small>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {itemType === 'found' ? 
                                                (match.description || t('potentialMatches.table.noDescription')) : 
                                                (match.storageLocation || t('potentialMatches.table.noData'))
                                            }
                                        </td>
                                        <td>
                                            {match.firstName || match.surname ? (
                                                <div>{match.firstName} {match.surname}</div>
                                            ) : (
                                                <div className="no-data">{t('potentialMatches.table.unknown')}</div>
                                            )}
                                        </td>
                                        <td>
                                            {match.phone ? (
                                                <div className="contact-info">
                                                    <div>{match.phone}</div>
                                                    {match.email && <div className="email">{match.email}</div>}
                                                </div>
                                            ) : (
                                                <div className="no-data">{t('potentialMatches.table.noContactInfo')}</div>
                                            )}
                                        </td>
                                        <td>
                                            <button 
                                                className={`select-match-button ${selectedMatch?.id === match.id ? 'selected' : ''}`}
                                                onClick={(e) => handleSelectMatch(e, match)}
                                            >
                                                {selectedMatch?.id === match.id ? t('potentialMatches.buttons.selected') : t('potentialMatches.buttons.select')}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {selectedMatch && (
                        <div style={{ marginTop: '20px', textAlign: 'center' }}>
                            <button 
                                className="match-button"
                                onClick={handleMatch}
                            >
                                {t('potentialMatches.buttons.confirmMatch')}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
} 
